import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Check, Clock, PhoneCall, Mail, CheckCircle2 } from "lucide-react";
import { API, axios, authHeaders, apiErr } from "@/lib/api";

const fmtCountdown = (deadline) => {
  const diff = new Date(deadline).getTime() - Date.now();
  if (diff <= 0) return { label: "Overdue", overdue: true };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const days = Math.floor(h / 24);
  const label = days > 0 ? `${days}d ${h % 24}h left` : `${h}h ${m}m left`;
  return { label, overdue: false };
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders`, authHeaders());
      setOrders(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 60000); return () => clearInterval(t); }, []);

  const toggleItem = async (orderId, index) => {
    setOrders((prev) => prev.map((o) => o.id !== orderId ? o : {
      ...o, checklist: o.checklist.map((c, i) => i === index ? { ...c, done: !c.done } : c),
    }));
    try { await axios.patch(`${API}/admin/orders/${orderId}/checklist`, { index }, authHeaders()); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  const toggleStatus = async (orderId, current) => {
    const next = current === "completed" ? "in_progress" : "completed";
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: next } : o)));
    try { await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: next }, authHeaders()); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  if (loading) return <div className="py-20 text-center text-zinc-500"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div data-testid="admin-orders-panel">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display font-black text-3xl">Orders <span className="text-zinc-600 text-xl">({orders.length})</span></h1>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white text-xs font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-white/5 transition-colors" data-testid="orders-refresh"><RefreshCw size={14} /> Refresh</button>
      </div>
      <div className="space-y-4">
        {orders.map((o) => {
          const countdown = fmtCountdown(o.deadline);
          const done = o.status === "completed";
          return (
            <div key={o.id} className={`bg-[#121212] border rounded-2xl p-5 md:p-6 ${done ? "border-[#ccff00]/40" : countdown.overdue ? "border-[#ff0055]/50" : "border-white/10"}`} data-testid={`order-card-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <p className="font-display font-bold text-lg">{o.business}</p>
                  <p className="text-sm text-zinc-500">{o.product_name}{o.amount ? ` · £${(o.amount / 100).toFixed(0)}` : ""}</p>
                </div>
                <button onClick={() => toggleStatus(o.id, o.status)} className={`inline-flex items-center gap-1.5 rounded-full text-xs font-bold uppercase tracking-widest px-4 py-2 transition-colors ${done ? "bg-[#ccff00] text-black" : countdown.overdue ? "bg-[#ff0055]/20 text-[#ff0055]" : "bg-white/10 text-zinc-300"}`} data-testid={`order-status-toggle-${o.id}`}>
                  <CheckCircle2 size={14} /> {done ? "Delivered" : countdown.overdue ? "Overdue" : "In progress"}
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400 mb-4">
                <span className="flex items-center gap-1.5"><PhoneCall size={13} /> {o.phone || "—"}</span>
                <span className="flex items-center gap-1.5"><Mail size={13} /> {o.email || "—"}</span>
                {!done && (
                  <span className={`flex items-center gap-1.5 font-semibold ${countdown.overdue ? "text-[#ff0055]" : "text-[#ccff00]"}`} data-testid={`order-countdown-${o.id}`}>
                    <Clock size={13} /> {countdown.label}
                  </span>
                )}
              </div>

              {o.details && <p className="text-sm text-zinc-500 mb-4 border-l-2 border-white/10 pl-3">{o.details}</p>}

              {o.referral_code && (
                <p className="text-xs text-zinc-500 mb-4">Referral code sent: <span className="text-[#ccff00] font-mono">{o.referral_code}</span></p>
              )}

              <div className="flex flex-wrap gap-2">
                {o.checklist.map((c, i) => (
                  <button key={i} onClick={() => toggleItem(o.id, i)} className={`inline-flex items-center gap-1.5 rounded-full text-xs px-3 py-1.5 border transition-colors ${c.done ? "bg-[#ccff00]/15 border-[#ccff00]/40 text-[#ccff00]" : "border-white/10 text-zinc-400 hover:border-white/25"}`} data-testid={`order-checklist-${o.id}-${i}`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${c.done ? "bg-[#ccff00] text-black" : "border border-white/25"}`}>{c.done && <Check size={10} />}</span>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {orders.length === 0 && <p className="text-zinc-500 py-16 text-center">No orders yet. They'll appear here once someone completes checkout + intake.</p>}
      </div>
    </div>
  );
};

export default AdminOrders;
